import { FormControl, FormGroup } from '@angular/forms';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    TradingService,
    TradingAccountDetailedReportModel,
    TradingAccountStatusTriggerModel
} from '@spa/api';

import { RequiredValidatorKey } from '@spa/validators';
import {
    AccountPlanStatusControlOptions,
    AccountPlanStatusesControl
} from 'appAssets/descriptors';
import { Helper, RadioGroupControl } from 'appShared/classes';
import { RadioGroupOptionInterface } from 'appShared/components';

export class AccountPlanStatusForm {
    private tradingService: TradingService;

    public formGroup: FormGroup;
    public control: RadioGroupControl;

    constructor(tradingService: TradingService) {
        this.tradingService = tradingService;
    }

    private mapStatusControlOptions(
        account: TradingAccountDetailedReportModel,
        triggers: number[],
        currentTrigger: number,
        options: RadioGroupOptionInterface[],
        disabledOption: boolean
    ): RadioGroupOptionInterface[] {
        return options.map(option => {
            const trigger = Helper.enumsHelper()
                .getAccountPlanStatusTriggerByStatus(option.value as number);

            if ((triggers.concat(currentTrigger).indexOf(trigger) < 0) || disabledOption) {
                option.disabled = true;
            }

            if (option.value === account.accountTradingPlanStatus) {
                option.checked = true;

                this.control.value = account.accountTradingPlanStatus;
            }

            return option;
        });
    }

    initForm(account: TradingAccountDetailedReportModel, requestTriggers: number[], disabledControlItems: boolean): void {
        this.formGroup = new FormGroup({});
        this.control = AccountPlanStatusesControl();

        const currentTrigger = Helper.enumsHelper()
            .getAccountPlanStatusTriggerByStatus(account.accountTradingPlanStatus);

        const options = AccountPlanStatusControlOptions();


        this.control.options = this.mapStatusControlOptions(
            account,
            requestTriggers,
            currentTrigger,
            options,
            disabledControlItems
        );

        this.formGroup.addControl(this.control.name, new FormControl(this.control.value, this.control.validators));
    }

    refreshForm(account: TradingAccountDetailedReportModel, triggers: number[], disabledControlItems: boolean): void {
        const currentTrigger = Helper.enumsHelper()
            .getAccountPlanStatusTriggerByStatus(account.accountTradingPlanStatus);

        this.control.options.forEach(option => {
            const trigger = Helper.enumsHelper()
                .getAccountPlanStatusTriggerByStatus(option.value as number);

            if ((triggers.concat(currentTrigger).indexOf(trigger) < 0) || disabledControlItems) {
                option.disabled = true;
            }
            else {
                option.disabled = false;
            }

            if (option.value === account.accountTradingPlanStatus) {
                option.checked = true;

                this.control.value = account.accountTradingPlanStatus;
            }
        });
    }

    validateForm(messagesFunction: (controlLabel?: string) => { [key: string]: string }): boolean {
        if (this.formGroup.invalid) {
            const control = this.formGroup.controls[this.control.name];

            this.control.errors = Object.keys(control.errors).map((key: string) => {
                if (key === RequiredValidatorKey) {
                    return 'Different status must be selected.';
                }

                return messagesFunction(this.control.placeholder)[key];
            });

            return false;
        }

        return true;
    }

    createRequest(
        account: TradingAccountDetailedReportModel
    ): Observable<ApiResponse<ApiError[] | boolean>> {
        const status = this.formGroup.value[this.control.name];

        if (account.accountTradingPlanStatus !== status) {
            const model = new TradingAccountStatusTriggerModel(
                account.accountId,
                Helper.enumsHelper().getAccountPlanStatusTriggerByStatus(status)
            );

            return this.tradingService.updateTradingAccountPlanStatus(model)
                .pipe(
                    map((response: ApiResponse<ApiError[] | null>) => {
                        if (response.status !== HttpStatusCodesEnum.Ok) {
                            return Helper.errorsHelper().createErrorResponse(
                                response
                            );
                        }
                        account.accountTradingPlanStatus = status;

                        return Helper.createSuccessResponse<boolean>(true);
                    })
                );
        }

        return Helper.createSuccessResponseObservable<boolean>(true);
    }
}
